// Named VFX emitters built on the billboard system, plus the ground decal pool.
//
// These are the "BattleForge half" of the art direction: smoke plumes, embers, fire, shockwaves,
// and persistent scorch marks. The existing ParticleSystem (sparks + gravity debris) keeps
// running alongside — its bursts are already wired into every event site, and it is cheap.

import * as THREE from 'three';
import { BillboardSystem } from './billboardParticles';
import { SPRITE, getSpriteAtlas } from './proceduralTextures';

const SMOKE_DARK = new THREE.Color(0x4a4640);
const SMOKE_PALE = new THREE.Color(0x8f8a80);
const EMBER_HOT = new THREE.Color(0xffb257);
const EMBER_CORE = new THREE.Color(0xfff0c4);
const FIRE = new THREE.Color(0xff7a2a);
const DUST = new THREE.Color(0x9a8d76);

function rand(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

export class Vfx {
  readonly billboards: BillboardSystem;

  constructor(scene: THREE.Scene) {
    this.billboards = new BillboardSystem(scene);
  }

  setQuality(quality: 'low' | 'medium' | 'high' | 'ultra', reducedMotion: boolean): void {
    this.billboards.setQuality(quality, reducedMotion);
  }

  update(dt: number): void {
    this.billboards.update(dt);
  }

  dispose(): void {
    this.billboards.dispose();
  }

  /** Rising smoke column. `intensity` scales count, size, and lifetime together. */
  smoke(x: number, z: number, intensity = 1, y = 0.4): void {
    const count = Math.round(3 + intensity * 4);
    for (let i = 0; i < count; i++) {
      this.billboards.emitSoft({
        x: x + rand(-0.5, 0.5) * intensity,
        y: y + rand(0, 0.5),
        z: z + rand(-0.5, 0.5) * intensity,
        vx: rand(-0.5, 0.5),
        vy: rand(0.7, 1.6) * intensity,
        vz: rand(-0.5, 0.5),
        ttl: rand(1.1, 2.2) * intensity,
        size: rand(0.8, 1.5) * intensity,
        growth: rand(1.4, 2.8) * intensity,
        color: Math.random() > 0.5 ? SMOKE_DARK : SMOKE_PALE,
        alpha: rand(0.16, 0.3),
        cell: SPRITE.puff,
        rotation: rand(0, Math.PI * 2),
        spin: rand(-0.5, 0.5),
        gravity: 0.35,
        drag: 0.22,
      });
    }
  }

  /** Ground-hugging dust, e.g. giant footfalls and charges. */
  dustPlume(x: number, z: number, intensity = 1): void {
    const count = Math.round(2 + intensity * 3);
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      this.billboards.emitSoft({
        x,
        y: 0.25,
        z,
        // Push outward rather than up, so it reads as displaced ground, not a fire.
        vx: Math.cos(angle) * rand(1, 2.6) * intensity,
        vy: rand(0.2, 0.7),
        vz: Math.sin(angle) * rand(1, 2.6) * intensity,
        ttl: rand(0.7, 1.3),
        size: rand(0.6, 1.1) * intensity,
        growth: rand(1.2, 2.2) * intensity,
        color: DUST,
        alpha: rand(0.18, 0.32),
        cell: SPRITE.puff,
        rotation: rand(0, Math.PI * 2),
        spin: rand(-0.8, 0.8),
        gravity: -0.25,
        drag: 0.06,
      });
    }
  }

  /** Hot embers rising and drifting; pairs with smoke for burning wreckage. */
  embers(x: number, z: number, intensity = 1, y = 0.5): void {
    const count = Math.round(4 + intensity * 6);
    for (let i = 0; i < count; i++) {
      this.billboards.emitAdditive({
        x: x + rand(-0.6, 0.6) * intensity,
        y: y + rand(0, 0.6),
        z: z + rand(-0.6, 0.6) * intensity,
        vx: rand(-1.1, 1.1),
        vy: rand(1.2, 3.0),
        vz: rand(-1.1, 1.1),
        ttl: rand(0.7, 1.7),
        size: rand(0.09, 0.2),
        growth: -0.06,
        color: Math.random() > 0.35 ? EMBER_HOT : EMBER_CORE,
        alpha: rand(0.7, 1),
        cell: SPRITE.ember,
        rotation: 0,
        spin: 0,
        gravity: -1.1,
        drag: 0.5,
      });
    }
  }

  /** Flame licks for burning structures and fire abilities. */
  fire(x: number, z: number, intensity = 1, y = 0.3): void {
    const count = Math.round(2 + intensity * 3);
    for (let i = 0; i < count; i++) {
      this.billboards.emitAdditive({
        x: x + rand(-0.4, 0.4) * intensity,
        y: y + rand(0, 0.3),
        z: z + rand(-0.4, 0.4) * intensity,
        vx: rand(-0.25, 0.25),
        vy: rand(1.4, 2.6),
        vz: rand(-0.25, 0.25),
        ttl: rand(0.35, 0.7),
        size: rand(0.5, 1.0) * intensity,
        growth: rand(0.3, 0.8),
        color: FIRE,
        alpha: rand(0.5, 0.85),
        cell: SPRITE.flame,
        rotation: rand(-0.2, 0.2),
        spin: rand(-0.4, 0.4),
        gravity: 0.6,
        drag: 0.3,
      });
    }
  }

  /** Expanding ring flash — explosions, slams, boss arrivals. */
  shockwave(x: number, z: number, color: number, radius = 4): void {
    this.billboards.emitAdditive({
      x,
      y: 0.6,
      z,
      vx: 0,
      vy: 0,
      vz: 0,
      ttl: 0.45,
      size: radius * 0.35,
      growth: radius * 1.6,
      color: new THREE.Color(color),
      alpha: 0.75,
      cell: SPRITE.ring,
      rotation: 0,
      spin: 0,
      gravity: 0,
      drag: 1,
    });
  }

  /** Bright flare, e.g. muzzle flash or a spell's moment of impact. */
  flash(x: number, y: number, z: number, color: number, size = 0.7): void {
    this.billboards.emitAdditive({
      x,
      y,
      z,
      vx: 0,
      vy: 0,
      vz: 0,
      ttl: 0.13,
      size,
      growth: size * 0.9,
      color: new THREE.Color(color),
      alpha: 1,
      cell: SPRITE.flare,
      rotation: Math.random() * Math.PI,
      spin: 0,
      gravity: 0,
      drag: 1,
    });
  }

  /** A single fading trail puff, emitted repeatedly along a projectile's flight. */
  trail(x: number, y: number, z: number, color: number, size = 0.22): void {
    this.billboards.emitAdditive({
      x,
      y,
      z,
      vx: 0,
      vy: 0,
      vz: 0,
      ttl: 0.28,
      size,
      growth: -size * 0.55,
      color: new THREE.Color(color),
      alpha: 0.6,
      cell: SPRITE.ember,
      rotation: 0,
      spin: 0,
      gravity: 0,
      drag: 1,
    });
  }
}

// ---------------------------------------------------------------- ground decals

interface Decal {
  index: number;
  age: number;
  ttl: number;
  /** seconds of full opacity before the fade begins; -1 = never fade */
  hold: number;
  alpha: number;
}

const MAX_DECALS = 256;

const DECAL_VERTEX = /* glsl */ `
  attribute vec3 iOffset;
  attribute float iScale;
  attribute float iRotation;
  attribute vec3 iColor;
  attribute float iAlpha;
  attribute float iCell;
  uniform vec2 atlasGrid;
  varying vec2 vUv;
  varying vec3 vColor;
  varying float vAlpha;
  void main() {
    // Flat on the ground plane: the quad's local XY maps to world XZ.
    float s = sin(iRotation);
    float c = cos(iRotation);
    vec2 corner = vec2(position.x * c - position.y * s, position.x * s + position.y * c) * iScale;
    vec3 world = iOffset + vec3(corner.x, 0.0, corner.y);
    float col = mod(iCell, atlasGrid.x);
    float row = floor(iCell / atlasGrid.x);
    vUv = (uv + vec2(col, row)) / atlasGrid;
    vColor = iColor;
    vAlpha = iAlpha;
    gl_Position = projectionMatrix * viewMatrix * vec4(world, 1.0);
  }
`;

const DECAL_FRAGMENT = /* glsl */ `
  uniform sampler2D atlas;
  varying vec2 vUv;
  varying vec3 vColor;
  varying float vAlpha;
  void main() {
    float mask = texture2D(atlas, vUv).r;
    float alpha = mask * vAlpha;
    if (alpha < 0.004) discard;
    gl_FragColor = vec4(vColor, alpha);
  }
`;

/**
 * Ground decal pool: scorch marks, craters, blood, corpse stains.
 *
 * Deliberately not THREE.DecalGeometry — that projects onto arbitrary meshes and allocates
 * geometry per decal, which is pointless when every decal lands on one flat plane. This is a
 * single instanced quad batch: one draw call for the whole battlefield's accumulated damage.
 */
export class DecalField {
  readonly mesh: THREE.Mesh;
  private geometry: THREE.InstancedBufferGeometry;
  private offsets: THREE.InstancedBufferAttribute;
  private scales: THREE.InstancedBufferAttribute;
  private rotations: THREE.InstancedBufferAttribute;
  private colors: THREE.InstancedBufferAttribute;
  private alphas: THREE.InstancedBufferAttribute;
  private cells: THREE.InstancedBufferAttribute;
  private live: Decal[] = [];
  private next = 0;
  private density = 1;

  constructor(scene: THREE.Scene) {
    const quad = new THREE.PlaneGeometry(1, 1);
    this.geometry = new THREE.InstancedBufferGeometry();
    this.geometry.index = quad.index;
    this.geometry.attributes.position = quad.attributes.position;
    this.geometry.attributes.uv = quad.attributes.uv;
    quad.dispose();

    const alloc = (size: number) =>
      new THREE.InstancedBufferAttribute(new Float32Array(MAX_DECALS * size), size).setUsage(THREE.DynamicDrawUsage);
    this.offsets = alloc(3);
    this.scales = alloc(1);
    this.rotations = alloc(1);
    this.colors = alloc(3);
    this.alphas = alloc(1);
    this.cells = alloc(1);
    this.geometry.setAttribute('iOffset', this.offsets);
    this.geometry.setAttribute('iScale', this.scales);
    this.geometry.setAttribute('iRotation', this.rotations);
    this.geometry.setAttribute('iColor', this.colors);
    this.geometry.setAttribute('iAlpha', this.alphas);
    this.geometry.setAttribute('iCell', this.cells);
    this.geometry.instanceCount = MAX_DECALS;

    const material = new THREE.ShaderMaterial({
      vertexShader: DECAL_VERTEX,
      fragmentShader: DECAL_FRAGMENT,
      uniforms: {
        atlas: { value: getSpriteAtlas() },
        atlasGrid: { value: new THREE.Vector2(4, 4) },
      },
      transparent: true,
      depthWrite: false,
      toneMapped: false,
      // Bias toward the camera so decals never z-fight with the ground plane they sit on.
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    });

    this.mesh = new THREE.Mesh(this.geometry, material);
    this.mesh.frustumCulled = false;
    // Above the ground and territory rings, below unit-space effects.
    this.mesh.renderOrder = 3;
    scene.add(this.mesh);
  }

  setQuality(quality: 'low' | 'medium' | 'high' | 'ultra', reducedMotion: boolean): void {
    const base = quality === 'low' ? 0.35 : quality === 'medium' ? 0.7 : 1;
    this.density = reducedMotion ? base * 0.5 : base;
  }

  /**
   * Stamp a decal. The pool is a ring buffer: once full, the oldest slot is reused, so a long
   * match degrades gracefully instead of unbounded growth.
   */
  add(
    x: number,
    z: number,
    cell: number,
    size: number,
    color: THREE.Color,
    alpha: number,
    ttl: number,
    hold = -1,
  ): void {
    if (this.density <= 0 || Math.random() > this.density) return;
    const index = this.next;
    this.next = (this.next + 1) % MAX_DECALS;
    this.offsets.array[index * 3] = x;
    // Just above the ground plane (which sits at y = -0.02); polygonOffset does the rest.
    this.offsets.array[index * 3 + 1] = 0.015;
    this.offsets.array[index * 3 + 2] = z;
    this.scales.array[index] = size;
    this.rotations.array[index] = Math.random() * Math.PI * 2;
    this.colors.array[index * 3] = color.r;
    this.colors.array[index * 3 + 1] = color.g;
    this.colors.array[index * 3 + 2] = color.b;
    this.alphas.array[index] = alpha;
    this.cells.array[index] = cell;

    const existing = this.live.findIndex((decal) => decal.index === index);
    if (existing >= 0) this.live.splice(existing, 1);
    this.live.push({ index, age: 0, ttl, hold, alpha });
    this.markDirty();
  }

  update(dt: number): void {
    if (this.live.length === 0) return;
    for (let i = this.live.length - 1; i >= 0; i--) {
      const decal = this.live[i];
      decal.age += dt;
      if (decal.hold >= 0 && decal.age > decal.hold) {
        const fade = 1 - (decal.age - decal.hold) / Math.max(0.001, decal.ttl - decal.hold);
        if (fade <= 0) {
          this.alphas.array[decal.index] = 0;
          this.live.splice(i, 1);
          continue;
        }
        this.alphas.array[decal.index] = decal.alpha * fade;
      }
    }
    this.markDirty();
  }

  private markDirty(): void {
    this.offsets.needsUpdate = true;
    this.scales.needsUpdate = true;
    this.rotations.needsUpdate = true;
    this.colors.needsUpdate = true;
    this.alphas.needsUpdate = true;
    this.cells.needsUpdate = true;
  }

  dispose(): void {
    this.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}

export { SPRITE };
